/**
 * The channel manager (plan §3.3, charter §12 C8 and §16.4).
 *
 * One place that owns: which channels are open, what happens when they drop,
 * what happens when the tab is hidden, and what happens when the token rotates.
 * Every domain's realtime path goes through this rather than calling
 * supabase.channel() for itself — six behaviours have to hold together, and they
 * do not survive being reimplemented per feature.
 *
 * ONE channel today: `org:{orgId}:refdata`, which carries org-scoped change
 * signals.
 *
 * Charter §16.4 also specifies a `user:{uid}` channel for the three slices that
 * are about the person rather than the org (their user record, admin control
 * sessions, billing), because a topic every org member subscribes to cannot
 * carry per-user rows. THAT SEAM IS INERT AND THIS MODULE NO LONGER OPENS IT.
 * `realtime.send` appears exactly twice in the whole migration tree, both inside
 * `t3_internal.emit_org_signal`, and both target `org:{id}:refdata` — nothing
 * anywhere publishes to `user:{uid}`. Opening it bought a silent channel per
 * signed-in tab against the per-project ceiling and, worse, made the module read
 * as though live membership revocation were handled when it is not: a revoked
 * member keeps their roles until the next reload either way.
 *
 * 0004's join policy still authorizes the user topic, so landing the producer (a
 * trigger on org_members / admin_control_sessions / org_billing) is the only
 * work left — at which point this restores the second topic and says so.
 *
 * The six behaviours, and why each is here rather than left to supabase-js:
 *
 *   1. resync on SUBSCRIBED — a join is exactly the moment you may have missed
 *      something. Not doing this is how a reconnect silently loses events.
 *   2. setAuth on token change — a private channel's authorization is cached for
 *      the life of the connection, and a channel that never receives a new JWT
 *      is disconnected when the old one expires. Firebase tokens live ~1h.
 *      MEASURED working (supabase/tests/realtime-join.mjs, H7).
 *   3. rejoin with backoff — CHANNEL_ERROR is not rare, and an unbacked-off
 *      retry loop against a rate limiter makes the problem worse.
 *   4. leave when hidden, rejoin when visible — charter §12 C8. A background tab
 *      holding a channel open is a connection slot spent on nobody, against a
 *      per-project ceiling.
 *   5. resync on visibility return — the same reasoning as (1).
 *   6. an explicit events-per-second cap — the spike measured a tenant-wide
 *      limiter; a client that does not declare its rate inherits a default that
 *      was not chosen.
 *
 * The realtime spike found supabase-js reports `resubscribed` zero times in
 * every phase of every round, so none of this can be delegated to the library.
 *
 * ===========================================================================
 * WHAT IS OBSERVED, AND WHY IT IS EXACTLY THIS
 * ===========================================================================
 * Charter §12 C8 names realtime as the wave's largest open risk, and it is the
 * only subsystem here that can fail PERMANENTLY and SILENTLY in one user's tab:
 * the backoff caps at 30 s and `attempt` is unbounded, so a channel that can
 * never rejoin retries forever for as long as the tab is open, and a resync
 * whose `events_since` call is refused surfaces only as a browser-level
 * unhandled rejection with no org, no uid and no trigger on it. "The register
 * stopped updating until I reloaded" was unanswerable.
 *
 * Two signals, chosen so the churn does not become the noise:
 *
 *   BREADCRUMBS for every non-SUBSCRIBED status and every rejoin. A channel
 *     bouncing is normal; one event per bounce would be worthless. They cost
 *     nothing and they ride along on whatever is captured next.
 *   ONE EVENT when the loop is stuck (backoff has reached its 30 s ceiling) and
 *     ONE EVENT per failed resync, tagged with WHICH of the three triggers
 *     fired — join, tab-visible or broadcast signal. Those three have entirely
 *     different causes and only one of them is a server problem, so the trigger
 *     is a tag rather than prose.
 *
 * The stuck state also gets ONE ACTION, not just one event: `onDegraded` (C17).
 * A channel that can never rejoin is indistinguishable from a membership that
 * was revoked — both refuse the join and neither issues an RPC anyone can
 * classify — so the ceiling is where the provider asks the authoritative probe
 * which of the two this is. See `scheduleRejoin`.
 */
import { captureDataFault, dataBreadcrumb } from "./telemetry.js";

const HIDDEN_GRACE_MS = 3 * 60 * 1000;   // leave after this long hidden (C8: "a few minutes")
const BACKOFF_MS = [500, 1000, 2000, 5000, 10000, 30000];

/**
 * @param {object} opts
 * @param {import("@supabase/supabase-js").SupabaseClient} opts.supabase
 * @param {{ onSignal: Function, resume: Function }} opts.resync
 * @param {(cb: (token: string|null) => void) => () => void} opts.subscribeToIdToken
 * @param {Function} [opts.onDegraded]  called ONCE when the rejoin loop reaches
 *        the backoff ceiling — see `scheduleRejoin`. The provider wires it to
 *        `accessGuard.confirm()`.
 * @param {object} [opts.doc]        — injected for tests; defaults to `document`.
 * @param {Function} [opts.setTimeoutFn] / [opts.clearTimeoutFn]
 */
export function createRealtime({
  supabase,
  resync,
  subscribeToIdToken,
  onDegraded = null,
  doc = typeof document === "undefined" ? null : document,
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout,
}) {
  let orgId = null;
  let channels = [];
  let attempt = 0;
  let hiddenTimer = null;
  let stopped = false;
  let unsubToken = null;
  let removeVisibility = null;
  // Once per stuck org, not once per retry: the retries are the symptom and
  // there is an unbounded number of them. Re-armed by a successful SUBSCRIBED
  // and by pointing at a different org.
  let stuckReported = false;

  /**
   * Run a resync trigger without leaving an unhandled rejection behind.
   *
   * All three call sites are fire-and-forget by design — nothing here can wait
   * on a fetch — so a rejection has nowhere to go. The async IIFE also converts
   * a SYNCHRONOUS throw from `run()` into the same rejection, which a bare
   * `.catch` on the returned value would miss.
   */
  const resyncTrigger = (trigger, run) => {
    (async () => run())().catch((error) => {
      captureDataFault(error, {
        component: "realtime",
        action: "resync",
        orgId,
        tags: { trigger, code: error?.code },
        context: {
          last_seq: resync?.lastSeq ?? null,
          rejoin_attempt: attempt,
          topics: topics(),
          open_channel_count: channels.length,
        },
      });
    });
  };

  // One topic. `user:{uid}` is not opened — see the header: nothing publishes
  // to it. Kept as a list so restoring it is a one-line change here rather than
  // a reshape of join()/leave()/openTopics.
  const topics = () => [`org:${orgId}:refdata`];

  const leave = () => {
    for (const ch of channels) { try { supabase.removeChannel(ch); } catch { /* already gone */ } }
    channels = [];
  };

  const join = () => {
    if (stopped || !orgId || channels.length > 0) return;

    channels = topics().map((topic) => {
      const channel = supabase.channel(topic, { config: { private: true } });

      channel.on("broadcast", { event: "refdata.changed" }, (msg) => {
        // Everything a signal can mean is resync's problem, not this module's.
        resyncTrigger("signal", () => resync.onSignal(msg?.payload ?? {}));
      });

      channel.subscribe((status) => {
        if (status === "SUBSCRIBED") {
          attempt = 0;
          stuckReported = false;
          // (1) A join is the moment to ask what was missed.
          resyncTrigger("subscribed", () => resync.resume());
        } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          scheduleRejoin(topic, status);
        } else {
          dataBreadcrumb("realtime", `channel ${status}`, { topic, status, attempt });
        }
      });

      return channel;
    });
  };

  const scheduleRejoin = (topic, status) => {
    if (stopped) return;
    const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
    attempt += 1;
    dataBreadcrumb("realtime", `channel ${status} — rejoin in ${delay}ms`, {
      topic, status, attempt, delay_ms: delay,
    });

    // The loop has just reached the end of the backoff array, so from here on
    // it retries every 30 s forever. That is the permanent-silent-failure state
    // C8 names, and it is the one thing in this module worth an event.
    if (attempt === BACKOFF_MS.length && !stuckReported) {
      stuckReported = true;
      captureDataFault("realtime channel stuck at maximum backoff", {
        component: "realtime",
        action: "rejoin",
        orgId,
        level: "error",
        tags: { topic, status },
        context: {
          rejoin_attempt: attempt,
          backoff_ceiling_ms: BACKOFF_MS[BACKOFF_MS.length - 1],
          topics: topics(),
          last_seq: resync?.lastSeq ?? null,
        },
      });

      // C17 — the OTHER thing a permanently unrejoinable channel means.
      //
      // A private channel that can never authorize looks exactly like one whose
      // member was removed: the join is refused, so SUBSCRIBED never fires, so
      // resync never resumes, so NO RPC is ever issued and nothing is ever
      // refused. The revoked session then stays fully live until the tab is
      // reloaded — the GH#117 hole the access guard exists to close, and the
      // half the provider's visibility trigger cannot reach on a tab that is
      // never left. Asking the authoritative probe is the only way to tell the
      // two apart, and this is the moment it is worth asking.
      //
      // Fire-and-forget, behind the SAME `stuckReported` latch as the event
      // above: one probe per stuck org, not one per retry (there is an
      // unbounded number of those). `confirm()` classifies its own failures and
      // never rejects, but this is not the place to find out otherwise — a
      // throw here would kill the rejoin timer below and freeze the channel for
      // good.
      try {
        void Promise.resolve(onDegraded?.()).catch(() => {});
      } catch { /* a synchronous throw from a caller's callback */ }
    }

    setTimeoutFn(() => { leave(); join(); }, delay);
  };

  const onVisibility = () => {
    if (!doc) return;
    if (doc.visibilityState === "hidden") {
      // (4) Do not drop instantly: tab switching is constant, and a rejoin costs
      // a round trip plus a resync. Only a genuinely backgrounded tab lets go.
      hiddenTimer = setTimeoutFn(() => { leave(); }, HIDDEN_GRACE_MS);
    } else {
      // `!== null`, not a truthiness check: a timer id of 0 is legitimate
      // (and is exactly what a test double returns), so `if (hiddenTimer)`
      // silently fails to cancel the first timer ever armed.
      if (hiddenTimer !== null) { clearTimeoutFn(hiddenTimer); hiddenTimer = null; }
      if (channels.length === 0) join();
      // (5) still joined, but may have missed something
      else resyncTrigger("visible", () => resync.resume());
    }
  };

  return {
    /** Open channels for an org. Safe to call again with a different org. */
    start(nextOrgId) {
      stopped = false;
      if (orgId && orgId !== nextOrgId) leave();
      orgId = nextOrgId;
      attempt = 0;
      stuckReported = false;

      // (2) The seam N1 shipped inert. This is what keeps a private channel from
      // being disconnected an hour after it opened.
      //
      // Guarded because this is the ONLY thing standing between an open channel
      // and the one-hour disconnect client.js's header describes, and it runs
      // inside Firebase's token listener — where a throw is swallowed by
      // Firebase and the channel simply dies an hour later with nothing said.
      unsubToken ??= subscribeToIdToken?.((token) => {
        if (!token) return;
        try {
          supabase.realtime.setAuth(token);
        } catch (error) {
          captureDataFault(error, {
            component: "realtime",
            action: "set_auth",
            orgId,
            tags: { code: error?.code },
            context: { topics: topics(), open_channel_count: channels.length },
          });
        }
      });

      if (doc) {
        removeVisibility ??= (() => {
          doc.addEventListener("visibilitychange", onVisibility);
          return () => doc.removeEventListener("visibilitychange", onVisibility);
        })();
      }

      join();
    },

    /** Tear down everything. Idempotent. */
    stop() {
      stopped = true;
      leave();
      if (hiddenTimer !== null) { clearTimeoutFn(hiddenTimer); hiddenTimer = null; }
      if (unsubToken) { unsubToken(); unsubToken = null; }
      if (removeVisibility) { removeVisibility(); removeVisibility = null; }
      orgId = null;
    },

    /** For tests and for the gate's "one org channel plus at most one user channel". */
    get openTopics() { return channels.map((c) => c.topic ?? c._topic ?? null); },
  };
}
